import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { Search, ChevronDown, ChevronUp, AlertTriangle, BookOpen, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface MonitoringParameter {
  name: string;
  frequency: string;
  actionThreshold: string;
  notes?: string;
}

interface MonitoringProtocol {
  drug: string;
  class: string;
  category: "DMARD" | "Biologic" | "JAK Inhibitor" | "Other";
  indication: string;
  baselineTests: string[];
  ongoingParameters: MonitoringParameter[];
  suspendCriteria: string[];
  guidanceSource: string;
  pharmacologyNote?: string;
}

const MONITORING_DATA: MonitoringProtocol[] = [
  {
    drug: "Methotrexate",
    class: "csDMARD",
    category: "DMARD",
    indication: "RA, PsA, inflammatory arthritis",
    baselineTests: ["FBC", "LFTs", "U&E", "Creatinine/eGFR", "Hepatitis B/C serology", "Chest X-ray", "Pregnancy test (if applicable)"],
    ongoingParameters: [
      { name: "FBC (including neutrophil count)", frequency: "Fortnightly for 6 weeks, then monthly for 3 months, then 3-monthly", actionThreshold: "WBC <3.5×10⁹/L, Neutrophils <2.0×10⁹/L, Platelets <150×10⁹/L — withhold and seek advice" },
      { name: "ALT/AST", frequency: "Same schedule as FBC", actionThreshold: ">2× ULN — withhold and review. >3× ULN on 2 occasions — consider stopping" },
      { name: "Albumin", frequency: "3–6 monthly", actionThreshold: "Falling albumin may indicate hepatotoxicity — review" },
      { name: "eGFR/Creatinine", frequency: "3–6 monthly", actionThreshold: "eGFR <45: reduce dose. eGFR <30: contraindicated" },
      { name: "MCV", frequency: "With FBC", actionThreshold: "Macrocytosis may indicate folate deficiency — ensure folic acid prescribed" },
    ],
    suspendCriteria: [
      "WBC <3.5×10⁹/L or Neutrophils <2.0×10⁹/L",
      "Platelets <150×10⁹/L",
      "ALT/AST >3× ULN on two consecutive tests",
      "Significant nausea/vomiting, mucositis, or unexplained bruising",
      "Intercurrent serious infection",
      "eGFR <30 mL/min",
    ],
    guidanceSource: "BSR Monitoring Guideline 2022; BNF",
    pharmacologyNote: "Methotrexate inhibits dihydrofolate reductase, reducing purine synthesis. Folate supplementation (5mg once weekly, not on MTX day) reduces mucositis and hepatotoxicity risk. Significant drug interactions with NSAIDs, trimethoprim, and probenecid.",
  },
  {
    drug: "Hydroxychloroquine",
    class: "csDMARD",
    category: "DMARD",
    indication: "SLE, RA, Sjögren's syndrome, mild inflammatory arthritis",
    baselineTests: ["FBC", "LFTs", "U&E", "Visual acuity (Snellen chart)", "Baseline retinal assessment (fundoscopy or OCT if available)", "G6PD status (if at-risk ethnicity)"],
    ongoingParameters: [
      { name: "Annual retinal screening (OCT)", frequency: "Annual after 5 years of use (from year 1 in high-risk patients)", actionThreshold: "Bull's eye maculopathy or parafoveal defects on OCT — stop immediately and refer ophthalmology", notes: "Screening from year 1 if renal impairment, tamoxifen use, cumulative dose >200g, or age >65" },
      { name: "FBC", frequency: "Annually", actionThreshold: "Significant cytopenias — review. Haemolytic anaemia rare but occurs in G6PD deficiency" },
      { name: "eGFR", frequency: "Annually", actionThreshold: "eGFR <30: reduce to 5mg/kg ideal body weight/day or consider stopping" },
    ],
    suspendCriteria: [
      "Any visual symptoms (blurring, colour vision change, parafoveal scotoma)",
      "Confirmed retinopathy on OCT",
      "Haemolytic anaemia",
      "QTc prolongation (caution with other QT-prolonging drugs)",
    ],
    guidanceSource: "RCOphth Hydroxychloroquine Retinal Toxicity Guidelines 2020; BSR Guideline",
    pharmacologyNote: "Hydroxychloroquine has an exceptionally long half-life (~40 days). Retinal toxicity is dose- and duration-dependent. Maximum dose 5mg/kg/day of actual body weight (not to exceed 400mg/day). Safe in pregnancy — do not stop without specialist review.",
  },
  {
    drug: "Sulfasalazine",
    class: "csDMARD",
    category: "DMARD",
    indication: "RA, PsA, ankylosing spondylitis",
    baselineTests: ["FBC", "LFTs", "U&E", "G6PD (if relevant ethnicity)"],
    ongoingParameters: [
      { name: "FBC", frequency: "Monthly for first 3 months, then 3-monthly", actionThreshold: "WBC <4.0×10⁹/L, Neutrophils <2.0×10⁹/L, Platelets <150×10⁹/L — withhold and review" },
      { name: "LFTs (ALT)", frequency: "Monthly for first 3 months, then 3-monthly", actionThreshold: ">2× ULN — reduce dose or withhold. >3× ULN — stop" },
      { name: "eGFR", frequency: "3–6 monthly", actionThreshold: "eGFR <30: use with caution, reduce dose" },
    ],
    suspendCriteria: [
      "Significant leukopenia or thrombocytopenia",
      "Severe hypersensitivity reactions (Stevens-Johnson syndrome risk)",
      "ALT/AST >3× ULN",
      "Haematuria (can cause orange urine — reassure patient, but investigate haematuria)",
    ],
    guidanceSource: "BSR Monitoring Guideline; BNF",
    pharmacologyNote: "Sulphonamide component — contraindicated in sulphonamide allergy. Common orange discolouration of urine and tears (warn soft contact lens wearers). May impair folate absorption. Safe in pregnancy (caution in third trimester). Interacts with methotrexate (additive folate antagonism).",
  },
  {
    drug: "Leflunomide",
    class: "csDMARD",
    category: "DMARD",
    indication: "RA, PsA",
    baselineTests: ["FBC", "LFTs", "U&E", "eGFR", "Blood pressure", "Hepatitis B/C serology"],
    ongoingParameters: [
      { name: "FBC", frequency: "Fortnightly for 6 months, then 6-weekly", actionThreshold: "WBC <3.5×10⁹/L, Neutrophils <2.0×10⁹/L, Platelets <150×10⁹/L — withhold" },
      { name: "LFTs (ALT)", frequency: "Monthly for 6 months, then 6-weekly", actionThreshold: ">2× ULN on 2 occasions — stop. Consider cholestyramine washout" },
      { name: "Blood pressure", frequency: "Monthly initially, then 3-monthly", actionThreshold: "Hypertension can occur — manage per NICE hypertension guidelines" },
      { name: "eGFR", frequency: "6-monthly", actionThreshold: "eGFR <30: use with caution. Active metabolite teriflunomide eliminated renally" },
    ],
    suspendCriteria: [
      "Significant cytopenias",
      "ALT >2× ULN on 2 consecutive tests",
      "Severe hypertension uncontrolled",
      "Teratogenicity risk — must be washed out with cholestyramine before conception in both sexes",
      "Severe infection",
    ],
    guidanceSource: "BSR Monitoring Guideline; SPC; BNF",
    pharmacologyNote: "Active metabolite teriflunomide has a very long half-life (up to 2 years). Washout with cholestyramine 8g TDS for 11 days is required before pregnancy or when stopping due to toxicity. Teratogenic — reliable contraception mandatory. CYP2C9 interactions relevant (warfarin, phenytoin).",
  },
  {
    drug: "Azathioprine",
    class: "csDMARD / Immunosuppressant",
    category: "DMARD",
    indication: "SLE, vasculitis, myositis, inflammatory arthritis (steroid-sparing)",
    baselineTests: ["FBC", "LFTs", "U&E", "eGFR", "TPMT activity (thiopurine methyltransferase)", "Hepatitis B/C serology"],
    ongoingParameters: [
      { name: "FBC (including WBC differential)", frequency: "Weekly for 4 weeks, fortnightly for 2 months, then monthly", actionThreshold: "WBC <3.0×10⁹/L or Neutrophils <1.5×10⁹/L — withhold immediately. Life-threatening myelosuppression possible" },
      { name: "LFTs", frequency: "3-monthly", actionThreshold: ">3× ULN — stop and review" },
      { name: "MCV", frequency: "With FBC", actionThreshold: "Macrocytosis indicates effective immunosuppression; significant macrocytosis may indicate toxicity" },
      { name: "eGFR", frequency: "3–6 monthly", actionThreshold: "eGFR <30: reduce dose by 50%" },
    ],
    suspendCriteria: [
      "WBC <3.0×10⁹/L or Neutrophils <1.5×10⁹/L (especially if TPMT-deficient or low activity)",
      "TPMT deficient — azathioprine generally contraindicated",
      "Severe hepatotoxicity",
      "Concurrent allopurinol use (major interaction — azathioprine dose must be reduced by 75% or alternative used)",
    ],
    guidanceSource: "BSR Monitoring Guideline; MHRA; BNF",
    pharmacologyNote: "Critical interaction with allopurinol — xanthine oxidase inhibition blocks azathioprine metabolism causing severe myelosuppression. TPMT genotyping guides dosing: low/intermediate activity requires dose reduction; absent TPMT — do not use. 6-thioguanine nucleotides can be measured to optimise dosing.",
  },
  {
    drug: "TNF Inhibitors (adalimumab, etanercept, certolizumab, golimumab, infliximab)",
    class: "Biologic DMARD",
    category: "Biologic",
    indication: "RA, PsA, axSpA, JIA, IBD-related arthritis",
    baselineTests: ["FBC", "LFTs", "U&E", "ESR/CRP", "TB screening (IGRA/Mantoux + CXR)", "Hepatitis B/C serology", "HIV (consider)", "Varicella serology if no history", "Pregnancy test", "Live vaccine immunisation review"],
    ongoingParameters: [
      { name: "FBC", frequency: "3–6 monthly", actionThreshold: "Neutropenia: Neutrophils <1.5×10⁹/L — withhold and review" },
      { name: "LFTs", frequency: "3–6 monthly", actionThreshold: ">3× ULN — review (hepatotoxicity less common than csDMARDs)" },
      { name: "ESR/CRP", frequency: "3–6 monthly", actionThreshold: "Monitor for disease activity response and to identify infection" },
      { name: "Clinical review (DAS28/CDAI)", frequency: "3-monthly until stable, then 6-monthly", actionThreshold: "NICE criteria: inadequate response — consider switching to alternative mechanism" },
    ],
    suspendCriteria: [
      "Serious infection (hold until resolved)",
      "TB reactivation — refer to respiratory/ID",
      "New-onset demyelinating disease",
      "Heart failure worsening (NYHA III/IV — contraindicated)",
      "Malignancy (most — discuss with oncology)",
      "Pre-operative period (hold 2–4 half-lives before elective surgery)",
    ],
    guidanceSource: "BSR Biologic Guidelines; NICE TA715; NHSE Biologics Policy",
    pharmacologyNote: "TNFi differ by mechanism: etanercept is a fusion protein (TNF receptor decoy), monoclonal antibodies (adalimumab, certolizumab, golimumab, infliximab) bind TNF directly. Immunogenicity and ADA (anti-drug antibodies) are relevant — therapeutic drug monitoring available for infliximab and adalimumab. Certolizumab is the preferred biologic in pregnancy.",
  },
  {
    drug: "IL-6 Inhibitors (tocilizumab, sarilumab)",
    class: "Biologic DMARD",
    category: "Biologic",
    indication: "RA, GCA (tocilizumab), sJIA",
    baselineTests: ["FBC", "LFTs", "Lipid profile", "U&E", "TB screening", "Hepatitis B/C serology", "CRP/ESR (note: IL-6 inhibition suppresses CRP — use ESR or clinical assessment)"],
    ongoingParameters: [
      { name: "Neutrophil count", frequency: "4–8 weekly initially, then 3-monthly", actionThreshold: "Neutrophils <1.0×10⁹/L — withhold. <0.5×10⁹/L — permanently discontinue" },
      { name: "Platelet count", frequency: "As above", actionThreshold: "Platelets <100×10⁹/L — withhold. <50×10⁹/L — discontinue" },
      { name: "ALT/AST", frequency: "3-monthly", actionThreshold: ">1–3× ULN — reduce dose or lengthen interval. >5× ULN — discontinue" },
      { name: "Lipid profile (total cholesterol, LDL, HDL, TG)", frequency: "8 weeks after starting, then annually", actionThreshold: "Significant dyslipidaemia — manage per NICE CG181. Statins as first-line" },
    ],
    suspendCriteria: [
      "Serious infection — hold until resolved (note: IL-6 inhibition may mask fever and raised CRP)",
      "Gut perforation (rare but serious — especially with diverticular disease)",
      "Severe neutropenia or thrombocytopenia",
      "Severe liver enzyme elevation",
    ],
    guidanceSource: "BSR Guidelines; NICE TA715; SPC",
    pharmacologyNote: "IL-6 inhibition suppresses CRP and ESR — do not use CRP alone to assess disease activity or infection. Clinical vigilance for infection is essential. Lipid effects are class-related (inflammatory suppression reduces HDL-C, but CV benefit likely outweighs risk). Tocilizumab has linear (IV) and non-linear (SC) pharmacokinetics.",
  },
  {
    drug: "IL-17 Inhibitors (secukinumab, ixekizumab, bimekizumab)",
    class: "Biologic DMARD",
    category: "Biologic",
    indication: "PsA, axSpA, plaque psoriasis",
    baselineTests: ["FBC", "LFTs", "U&E", "TB screening", "Hepatitis B serology", "Inflammatory bowel disease history (relative contraindication)"],
    ongoingParameters: [
      { name: "FBC", frequency: "3–6 monthly", actionThreshold: "Significant neutropenia — review" },
      { name: "Clinical response (BASDAI/PASI/ACR)", frequency: "3-monthly until stable", actionThreshold: "Inadequate response by 16 weeks — consider switching mechanism" },
      { name: "Mucocutaneous candidiasis", frequency: "Clinical assessment at each visit", actionThreshold: "Recurrent/severe candidiasis — consider dose reduction or switch" },
    ],
    suspendCriteria: [
      "New or worsening inflammatory bowel disease (Crohn's disease especially — contraindicated)",
      "Serious infection",
      "Severe hypersensitivity reaction",
    ],
    guidanceSource: "BSR Guidelines; NICE TA445; SPC",
    pharmacologyNote: "IL-17A (and IL-17A/F for bimekizumab) inhibition. Effective for skin and joint PsA, and axSpA. Mucocutaneous candidiasis is a class effect (IL-17 is important for fungal immunity). IBD is a class contraindication — screen carefully before starting. Bimekizumab dual IL-17A/F inhibition associated with higher candidiasis rates.",
  },
  {
    drug: "IL-23 Inhibitors (guselkumab, risankizumab, ustekinumab)",
    class: "Biologic DMARD",
    category: "Biologic",
    indication: "PsA (guselkumab, risankizumab, ustekinumab), plaque psoriasis",
    baselineTests: ["FBC", "LFTs", "U&E", "TB screening", "Hepatitis B serology"],
    ongoingParameters: [
      { name: "FBC", frequency: "6-monthly", actionThreshold: "Cytopenias — review" },
      { name: "LFTs", frequency: "6-monthly", actionThreshold: ">3× ULN — review" },
      { name: "Clinical response (ACR/PASI)", frequency: "3-monthly until stable, then 6-monthly", actionThreshold: "Inadequate response by 16–24 weeks — consider switching" },
    ],
    suspendCriteria: [
      "Serious infection",
      "New malignancy",
      "Severe hypersensitivity",
    ],
    guidanceSource: "BSR Guidelines; NICE TA; SPC",
    pharmacologyNote: "IL-23 inhibitors target the p19 subunit of IL-23 specifically (guselkumab, risankizumab) or both IL-12 and IL-23 via p40 subunit (ustekinumab). Favourable safety profile with less impact on IL-17 pathway. Good persistence data. Ustekinumab approved for Crohn's disease — can be used in IBD-associated PsA.",
  },
  {
    drug: "JAK Inhibitors (baricitinib, tofacitinib, upadacitinib, filgotinib)",
    class: "Targeted Synthetic DMARD",
    category: "JAK Inhibitor",
    indication: "RA, PsA, axSpA, JIA (upadacitinib), alopecia areata",
    baselineTests: ["FBC", "LFTs", "U&E", "eGFR", "Lipid profile", "TB screening (IGRA)", "Hepatitis B/C serology", "VZV serology (consider vaccination before starting)", "12-lead ECG if cardiac risk factors"],
    ongoingParameters: [
      { name: "FBC (lymphocytes, neutrophils, haemoglobin)", frequency: "4–8 weeks initially, then 3-monthly", actionThreshold: "Lymphocytes <0.5×10⁹/L — withhold. Hgb <8g/dL — withhold. Neutrophils <1.0×10⁹/L — withhold" },
      { name: "LFTs", frequency: "3–6 monthly", actionThreshold: ">3× ULN — review; consider dose reduction or switch" },
      { name: "eGFR/Creatinine", frequency: "3–6 monthly", actionThreshold: "Baricitinib, filgotinib — renally cleared. eGFR 30–60: dose reduce. eGFR <30: avoid baricitinib/filgotinib" },
      { name: "Lipid profile", frequency: "8 weeks after starting, then annually", actionThreshold: "LDL rise is class-related. Manage with statins per NICE CG181 if indicated" },
      { name: "VTE assessment", frequency: "Ongoing clinical assessment", actionThreshold: "MHRA warning: increased VTE risk especially with tofacitinib at high doses. Avoid in high VTE-risk patients (active malignancy, history of DVT/PE, prolonged immobility)" },
      { name: "MACE/CV risk assessment", frequency: "Annually", actionThreshold: "MHRA black triangle: increased CV events in older patients or those with CV risk factors vs TNFi. Prefer JAKi in appropriate patient selection per MHRA guidance" },
    ],
    suspendCriteria: [
      "Serious infection (VZV reactivation common — antiviral prophylaxis in high-risk patients)",
      "Confirmed VTE (DVT/PE)",
      "Severe haematological abnormalities",
      "MACE or major CV event",
      "New malignancy (MHRA: increased risk of malignancy, especially NMSC)",
      "Pregnancy (stop at least 4 weeks before conception; teratogenic in animal studies)",
    ],
    guidanceSource: "MHRA JAK Inhibitor Safety Guidance 2021/2023; NICE TA715; BSR Guidelines; EMA Safety Review",
    pharmacologyNote: "MHRA 2023 class-wide safety measures: restrict to patients ≥65 years, smokers/ex-smokers, at increased CV risk, or with malignancy risk factors only after other treatments have failed (or are inappropriate). JAK selectivity: tofacitinib (JAK1/3), baricitinib (JAK1/2), upadacitinib (JAK1-selective), filgotinib (JAK1-selective). Renal dose adjustment required for baricitinib and filgotinib. VZV vaccination recommended before starting (live vaccine — not after).",
  },
  {
    drug: "Rituximab",
    class: "Biologic DMARD (B-cell depletion)",
    category: "Biologic",
    indication: "RA (after failed TNFi or TNFi-inappropriate), ANCA vasculitis, SLE, myositis",
    baselineTests: ["FBC with B-cell count (CD19/CD20)", "LFTs", "U&E", "eGFR", "Immunoglobulins (IgG, IgA, IgM)", "Hepatitis B serology (core Ab AND surface Ag — reactivation risk)", "Hepatitis C serology", "TB screening", "JC virus antibody (consider in at-risk patients)"],
    ongoingParameters: [
      { name: "FBC and B-cell count", frequency: "Before each course (typically 6-monthly)", actionThreshold: "Neutrophils <1.5×10⁹/L — withhold course. Persistent hypogammaglobulinaemia — withhold and consider Ig replacement" },
      { name: "Immunoglobulins (IgG)", frequency: "Before each course and 6-monthly", actionThreshold: "IgG <6g/L — increased infection risk. IgG <4g/L — consider stopping and refer for Ig replacement therapy assessment" },
      { name: "B-cell repopulation", frequency: "Before retreatment", actionThreshold: "CD19+ B cells >10 cells/μL may indicate eligibility for retreatment (disease activity driven)" },
      { name: "Hepatitis B monitoring", frequency: "Before each course if HBcAb positive", actionThreshold: "HBV reactivation — antiviral prophylaxis (entecavir) required if HBsAg positive or HBcAb positive; liaise with hepatology" },
    ],
    suspendCriteria: [
      "Serious infection",
      "PML (progressive multifocal leukoencephalopathy) — rare but life-threatening",
      "Severe IgG deficiency with recurrent infections",
      "Active HBV infection (without antiviral cover)",
      "Severe infusion reactions",
    ],
    guidanceSource: "BSR Rituximab Guideline; NHSE; NICE TA195/TA715; SPC",
    pharmacologyNote: "Rituximab depletes CD20+ B cells — response may take 12–16 weeks. B-cell depletion persists for 6–12 months; retreatment typically at 6 months based on B-cell repopulation and disease activity. Hypogammaglobulinaemia is cumulative with repeated courses — IgG monitoring is essential. Live vaccines are contraindicated while B cells are depleted and for 12 months after.",
  },
  {
    drug: "Abatacept",
    class: "Biologic DMARD (T-cell co-stimulation blocker)",
    category: "Biologic",
    indication: "RA (after csDMARD failure or as first biologic — NICE TA715)",
    baselineTests: ["FBC", "LFTs", "U&E", "TB screening", "Hepatitis B/C serology", "COPD/respiratory assessment (higher risk of COPD exacerbations)"],
    ongoingParameters: [
      { name: "FBC", frequency: "3–6 monthly", actionThreshold: "Cytopenias — review" },
      { name: "LFTs", frequency: "3–6 monthly", actionThreshold: ">3× ULN — review" },
      { name: "Respiratory function", frequency: "Clinical review", actionThreshold: "Worsening COPD — review therapy" },
      { name: "Clinical response (DAS28)", frequency: "3-monthly until stable", actionThreshold: "Inadequate response by 6 months — consider switching" },
    ],
    suspendCriteria: [
      "Serious infection",
      "Severe COPD exacerbation",
      "New malignancy",
    ],
    guidanceSource: "NICE TA715; BSR Guidelines; SPC",
    pharmacologyNote: "Abatacept blocks CD80/CD86-CD28 co-stimulatory signal required for T-cell activation. SC formulation available. Preferred in seropositive RA (RF/ACPA positive). Lower risk of infection vs other biologics in some analyses. Avoid combination with other biologics — increased infection risk. Not recommended with TNFi concurrently.",
  },
  {
    drug: "Belimumab",
    class: "Biologic DMARD (BLyS inhibitor)",
    category: "Biologic",
    indication: "SLE (active, autoantibody-positive despite standard therapy), LN (IV formulation)",
    baselineTests: ["FBC", "LFTs", "U&E", "Complement (C3, C4)", "Anti-dsDNA antibodies", "SLEDAI-2K score", "Hepatitis B/C serology", "TB screening", "Urinalysis/urine protein:creatinine ratio"],
    ongoingParameters: [
      { name: "FBC", frequency: "3–6 monthly", actionThreshold: "Lymphopenia is common; IgG reduction expected. Significant neutropenia — review" },
      { name: "SLEDAI-2K/BILAG", frequency: "3–6 monthly", actionThreshold: "Inadequate response by 6 months — review continuation" },
      { name: "Anti-dsDNA and complement", frequency: "3–6 monthly", actionThreshold: "Serological improvement expected over time; rising titre with active disease — review" },
      { name: "Urine protein:creatinine ratio", frequency: "3–6 monthly", actionThreshold: "Rising proteinuria in LN — review nephrology input" },
    ],
    suspendCriteria: [
      "Serious infection or sepsis",
      "Severe depression or suicidality (class warning — neuropsychiatric SLE must be distinguished)",
      "PML (case reports)",
      "Severe hypersensitivity/anaphylaxis",
    ],
    guidanceSource: "NICE TA397/TA686; BSR SLE Guideline; SPC",
    pharmacologyNote: "Belimumab inhibits B-lymphocyte stimulator (BLyS/BAFF), reducing survival of autoreactive B cells. SC and IV formulations available. Response in SLE may take 6–12 months — persistence is key. Not effective in anti-dsDNA negative SLE. IV anifrolumab is an alternative IFN-blocking biologic for SLE.",
  },
];

const categoryColours: Record<string, { bg: string; text: string; border: string }> = {
  "DMARD": { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200" },
  "Biologic": { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  "JAK Inhibitor": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  "Other": { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" },
};

function DrugCard({ protocol }: { protocol: MonitoringProtocol }) {
  const [expanded, setExpanded] = useState(false);
  const colours = categoryColours[protocol.category];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls={`drug-panel-${protocol.drug.replace(/\s+/g, "-")}`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="text-lg font-bold text-slate-900">{protocol.drug}</h3>
            <Badge className={`${colours.bg} ${colours.text} border ${colours.border} text-xs font-medium`}>
              {protocol.category}
            </Badge>
            <span className="text-xs text-slate-500">{protocol.class}</span>
          </div>
          <p className="text-sm text-slate-600"><span className="font-medium">Indication:</span> {protocol.indication}</p>
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
            <BookOpen className="h-3 w-3" /> {protocol.guidanceSource}
          </p>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div id={`drug-panel-${protocol.drug.replace(/\s+/g, "-")}`} className="px-6 pb-6 space-y-6 border-t border-slate-100">
          <div className="pt-4">
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-violet-500" /> Baseline Investigations
            </h4>
            <div className="flex flex-wrap gap-2">
              {protocol.baselineTests.map((test) => (
                <span key={test} className="bg-slate-100 text-slate-700 text-xs px-2.5 py-1 rounded-full">{test}</span>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-800 mb-3">Ongoing Monitoring</h4>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-left">
                    <th scope="col" className="px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wide w-1/4">Parameter</th>
                    <th scope="col" className="px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wide w-1/4">Frequency</th>
                    <th scope="col" className="px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wide">Action Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {protocol.ongoingParameters.map((param, idx) => (
                    <tr key={param.name} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="px-4 py-3 font-semibold text-slate-900 align-top">{param.name}</td>
                      <td className="px-4 py-3 text-violet-700 align-top">{param.frequency}</td>
                      <td className="px-4 py-3 text-slate-600 align-top">
                        <span className="font-medium text-amber-700">Action: </span>{param.actionThreshold}
                        {param.notes && <p className="text-xs text-slate-500 mt-1 italic">{param.notes}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> When to Withhold / Suspend
            </h4>
            <ul className="space-y-1">
              {protocol.suspendCriteria.map((criterion) => (
                <li key={criterion} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-red-400 mt-0.5 shrink-0">•</span>
                  {criterion}
                </li>
              ))}
            </ul>
          </div>

          {protocol.pharmacologyNote && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <h4 className="text-xs font-bold text-violet-800 mb-2 uppercase tracking-wider">Clinical Pharmacology Note</h4>
              <p className="text-sm text-violet-900 leading-relaxed">{protocol.pharmacologyNote}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PharmacistMonitoring() {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");

  const filtered = MONITORING_DATA.filter((p) => {
    const matchSearch =
      search === "" ||
      p.drug.toLowerCase().includes(search.toLowerCase()) ||
      p.class.toLowerCase().includes(search.toLowerCase()) ||
      p.indication.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === "all" || p.category === filterCategory;
    return matchSearch && matchCategory;
  });

  const categories = ["all", "DMARD", "Biologic", "JAK Inhibitor"];

  return (
    <PharmacistLayout title="DMARD & Biologic Monitoring">
      <div className="space-y-6">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm text-violet-800">
          <strong>Pharmacist monitoring reference.</strong> Based on BSR Monitoring Guidelines 2022, NHSE/NICE guidance, and individual drug SPCs. Always check the most current SPC and local trust protocols. Thresholds are guidance — clinical judgment applies.
        </div>

        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search drugs, class, or indication..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterCategory === cat
                    ? "bg-violet-600 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-violet-50"
                }`}
              >
                {cat === "all" ? "All" : cat}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-slate-500">{filtered.length} drug{filtered.length !== 1 ? "s" : ""} shown</p>

        <div className="space-y-4">
          {filtered.map((protocol) => (
            <DrugCard key={protocol.drug} protocol={protocol} />
          ))}
        </div>
      </div>
    </PharmacistLayout>
  );
}
